import type {StateCreator} from 'zustand'
import type {ReviewSummary} from '../../page-data/payloads/review-summaries'

export type ReviewSummariesActions = {
  setReviewSummaries: (reviewSummaries: ReviewSummary[]) => void
  addReviewSummary: (reviewSummary: ReviewSummary) => void
  addReviewSummaries: (reviewSummaries: ReviewSummary[]) => void
  updateReviewSummary: (args: {
    match: (reviewSummary: ReviewSummary) => boolean
    transform: (reviewSummary: ReviewSummary | undefined) => ReviewSummary | undefined
  }) => void
}

export type ReviewSummarySlice = {
  reviewSummaries: ReviewSummary[]
  reviewSummariesActions: ReviewSummariesActions
}

export type ReviewSummariesSliceOpts = Record<string, unknown>

export const createReviewSummarySlice =
  (
    _: ReviewSummariesSliceOpts,
  ): StateCreator<ReviewSummarySlice, [['zustand/devtools', never]], [], ReviewSummarySlice> =>
  set => ({
    reviewSummaries: [],
    reviewSummariesActions: {
      setReviewSummaries: reviewSummaries => {
        set({reviewSummaries}, undefined, 'setReviewSummaries')
      },
      addReviewSummaries: reviewSummaries => {
        set(
          state => {
            const seenIds = new Set(state.reviewSummaries.map(summary => summary.id))
            const uniqueNewSummaries = reviewSummaries.filter(summary => {
              if (seenIds.has(summary.id)) return false
              seenIds.add(summary.id)
              return true
            })

            return {
              reviewSummaries: [...state.reviewSummaries, ...uniqueNewSummaries],
            }
          },
          undefined,
          'addReviewSummaries',
        )
      },
      addReviewSummary: reviewSummary => {
        set(
          state => ({
            reviewSummaries: [...state.reviewSummaries, reviewSummary],
          }),
          undefined,
          'addReviewSummary',
        )
      },
      updateReviewSummary: ({match, transform}) => {
        set(
          state => ({
            reviewSummaries: state.reviewSummaries.map(reviewSummary => {
              if (match(reviewSummary)) {
                const updated = transform(reviewSummary)
                return updated ?? reviewSummary
              }
              return reviewSummary
            }),
          }),
          undefined,
          'updateReviewSummary',
        )
      },
    },
  })
